import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ArrowRight,
  Download,
  ExternalLink,
  Film,
  Loader2,
  Play,
  RefreshCw,
  Sparkles,
  Upload,
  X,
  AlertTriangle,
} from 'lucide-react';
import {
  MOTION_OPTIONS,
  RATIO_OPTIONS,
  STAGING_ANIMATION_STYLE_OPTIONS,
  compareTransitionImages,
  type StagingAnimationMotion,
  type StagingAnimationRatio,
  type StagingAnimationStyle,
  type TransitionAnimationSettings,
  DEFAULT_TRANSITION_SETTINGS,
} from '@rehub/transition-animation';
import { getFunctionInvokeErrorMessage, supabase, uploadToStorage } from '../services/supabase';
import { trackImagemotionEvent } from '../services/analytics';

export interface StagingAnimationJob {
  id: string;
  transition_type: string;
  source: string;
  source_image_id: string | null;
  project_name: string | null;
  before_image_url: string | null;
  after_image_url: string | null;
  before_storage_path: string | null;
  after_storage_path: string | null;
  style: StagingAnimationStyle;
  duration_seconds: number;
  aspect_ratio: string;
  motion_level: string;
  provider: string | null;
  model: string | null;
  status: 'queued' | 'processing' | 'completed' | 'failed';
  credit_cost: number;
  charged: boolean;
  output_url: string | null;
  thumbnail_url: string | null;
  error_message: string | null;
  created_at: string;
  completed_at: string | null;
}

interface FrameState {
  preview: string | null;
  url: string | null;
  path: string | null;
  width: number | null;
  height: number | null;
  uploading: boolean;
}

const STAGING_APP_URL = 'https://staging.rehub.studio';

async function openStagingApp() {
  let hash = '';
  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (session?.access_token && session?.refresh_token) {
      hash =
        `#sso_access_token=${encodeURIComponent(session.access_token)}` +
        `&sso_refresh_token=${encodeURIComponent(session.refresh_token)}`;
    }
  } catch {
    // Staging will prompt for login if the session cannot be forwarded.
  }
  window.location.href = `${STAGING_APP_URL}/${hash}`;
}

const emptyFrame = (): FrameState => ({
  preview: null,
  url: null,
  path: null,
  width: null,
  height: null,
  uploading: false,
});

function frameFromJob(url: string | null, path: string | null): FrameState {
  return {
    preview: url,
    url,
    path,
    width: null,
    height: null,
    uploading: false,
  };
}

function readImageSize(src: string): Promise<{ width: number; height: number }> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve({ width: img.naturalWidth, height: img.naturalHeight });
    img.onerror = () => reject(new Error('Could not read image'));
    img.src = src;
  });
}

async function downloadVideo(url: string, name: string) {
  const res = await fetch(url);
  const blob = await res.blob();
  const objectUrl = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = objectUrl;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(objectUrl);
}

interface StagingAnimationProps {
  userId: string;
  onBack: () => void;
  onCreditsUpdated?: () => void;
}

export default function StagingAnimation({ userId, onBack, onCreditsUpdated }: StagingAnimationProps) {
  const params = useMemo(() => new URLSearchParams(window.location.search), []);
  const [settings, setSettings] = useState<TransitionAnimationSettings>(DEFAULT_TRANSITION_SETTINGS);
  const [before, setBefore] = useState<FrameState>(emptyFrame);
  const [after, setAfter] = useState<FrameState>(emptyFrame);
  const [style, setStyle] = useState<StagingAnimationStyle>(DEFAULT_TRANSITION_SETTINGS.default_style);
  const [exampleOpen, setExampleOpen] = useState(false);
  const [duration, setDuration] = useState<number>(DEFAULT_TRANSITION_SETTINGS.default_duration);
  const [ratio, setRatio] = useState<StagingAnimationRatio>('original');
  const [motion, setMotion] = useState<StagingAnimationMotion>(DEFAULT_TRANSITION_SETTINGS.default_motion);
  const [job, setJob] = useState<StagingAnimationJob | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [starting, setStarting] = useState(false);
  const [compareOpen, setCompareOpen] = useState(false);
  const idempotencyRef = useRef<string | null>(null);
  const openedRef = useRef(false);

  const source = params.get('source') === 'virtual_staging' ? 'virtual_staging' : 'imagemotion';
  const sourceImageId = params.get('sourceImage');
  const projectName = params.get('name') || 'Staging Animation';

  useEffect(() => {
    if (!exampleOpen) return;
    function onKey(event: KeyboardEvent) {
      if (event.key === 'Escape') setExampleOpen(false);
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [exampleOpen]);

  useEffect(() => {
    if (openedRef.current) return;
    openedRef.current = true;
    trackImagemotionEvent('staging_animation_opened', userId, { source });
  }, [userId, source]);

  useEffect(() => {
    supabase
      .from('transition_animation_settings')
      .select('*')
      .eq('id', 1)
      .maybeSingle()
      .then(({ data }) => {
        if (!data) return;
        const next: TransitionAnimationSettings = {
          enabled: data.enabled !== false,
          credit_cost: Number(data.credit_cost ?? DEFAULT_TRANSITION_SETTINGS.credit_cost),
          default_duration: (Number(data.default_duration) || 7) as TransitionAnimationSettings['default_duration'],
          allowed_durations: data.allowed_durations ?? [5, 7, 10],
          available_styles: data.available_styles ?? DEFAULT_TRANSITION_SETTINGS.available_styles,
          default_style: data.default_style || 'furniture_build',
          available_aspect_ratios: data.available_aspect_ratios ?? DEFAULT_TRANSITION_SETTINGS.available_aspect_ratios,
          default_motion: data.default_motion || 'standard',
          provider: data.provider || 'fal',
          model_endpoint: data.model_endpoint,
          adapter_type: data.adapter_type,
        };
        setSettings(next);
        if (params.get('id')) return;
        setStyle(next.default_style);
        setDuration(next.default_duration);
        setMotion(next.default_motion);
      });
  }, []);

  useEffect(() => {
    const jobId = params.get('id');
    if (!jobId) return;
    supabase
      .from('transition_animations')
      .select('*')
      .eq('id', jobId)
      .maybeSingle()
      .then(({ data }) => {
        if (!data) return;
        const loaded = data as StagingAnimationJob;
        setJob(loaded);
        setBefore(frameFromJob(loaded.before_image_url, loaded.before_storage_path));
        setAfter(frameFromJob(loaded.after_image_url, loaded.after_storage_path));
        setStyle(loaded.style);
        setDuration(loaded.duration_seconds);
        if (loaded.aspect_ratio === '16:9' || loaded.aspect_ratio === '9:16' || loaded.aspect_ratio === '1:1' || loaded.aspect_ratio === 'original') {
          setRatio(loaded.aspect_ratio);
        }
        if (loaded.motion_level === 'subtle' || loaded.motion_level === 'standard' || loaded.motion_level === 'dynamic') {
          setMotion(loaded.motion_level);
        }
      });
  }, [params]);

  useEffect(() => {
    if (!sourceImageId) return;
    supabase
      .from('images')
      .select('id, input_url, output_url')
      .eq('id', sourceImageId)
      .maybeSingle()
      .then(async ({ data }) => {
        if (!data?.input_url || !data?.output_url) {
          setError('Could not load the staged images for this project.');
          return;
        }
        const [beforeSize, afterSize] = await Promise.all([
          readImageSize(data.input_url).catch(() => null),
          readImageSize(data.output_url).catch(() => null),
        ]);
        setBefore({
          preview: data.input_url,
          url: data.input_url,
          path: null,
          width: beforeSize?.width ?? null,
          height: beforeSize?.height ?? null,
          uploading: false,
        });
        setAfter({
          preview: data.output_url,
          url: data.output_url,
          path: null,
          width: afterSize?.width ?? null,
          height: afterSize?.height ?? null,
          uploading: false,
        });
      });
  }, [sourceImageId]);

  const poll = useCallback(async (jobId: string) => {
    const { data: sessionData } = await supabase.auth.getSession();
    const accessToken = sessionData.session?.access_token;
    if (!accessToken) return;
    const { data, error: invokeError } = await supabase.functions.invoke('imagemotion-staging-animation', {
      body: { action: 'poll', jobId },
      headers: { Authorization: `Bearer ${accessToken}` },
    });
    if (invokeError) return;
    const next = (data as { job?: StagingAnimationJob })?.job;
    if (!next) return;
    setJob(next);
    if (next.status === 'completed') {
      trackImagemotionEvent('staging_animation_completed', userId, { source, job_id: next.id });
      onCreditsUpdated?.();
    }
    if (next.status === 'failed') {
      trackImagemotionEvent('staging_animation_failed', userId, { source, job_id: next.id });
      onCreditsUpdated?.();
    }
  }, [onCreditsUpdated, source, userId]);

  useEffect(() => {
    if (!job || (job.status !== 'processing' && job.status !== 'queued')) return;
    const timer = window.setInterval(() => {
      void poll(job.id);
    }, 4000);
    return () => window.clearInterval(timer);
  }, [job, poll]);

  const match = compareTransitionImages(
    before.width && before.height ? { width: before.width, height: before.height } : null,
    after.width && after.height ? { width: after.width, height: after.height } : null,
  );

  const styles = STAGING_ANIMATION_STYLE_OPTIONS.filter((option) =>
    settings.available_styles.includes(option.id),
  );
  const durations = settings.allowed_durations;
  const ratios = RATIO_OPTIONS.filter((option) => settings.available_aspect_ratios.includes(option.id));

  async function onFile(which: 'before' | 'after', file: File) {
    const setter = which === 'before' ? setBefore : setAfter;
    const preview = URL.createObjectURL(file);
    setter({ preview, url: null, path: null, width: null, height: null, uploading: true });
    try {
      const size = await readImageSize(preview);
      const uploaded = await uploadToStorage(file, 'images', userId);
      setter({
        preview,
        url: uploaded.url,
        path: uploaded.path,
        width: size.width,
        height: size.height,
        uploading: false,
      });
      trackImagemotionEvent(
        which === 'before' ? 'staging_animation_before_uploaded' : 'staging_animation_after_uploaded',
        userId,
        { source },
      );
    } catch (err) {
      setter(emptyFrame());
      setError(err instanceof Error ? err.message : 'Upload failed');
    }
  }

  async function generate(nextStyle = style) {
    if (!settings.enabled) {
      setError('Staging Animation is currently unavailable.');
      return;
    }
    const beforeUrl = before.url || job?.before_image_url || null;
    const afterUrl = after.url || job?.after_image_url || null;
    const beforePath = before.path || job?.before_storage_path || null;
    const afterPath = after.path || job?.after_storage_path || null;
    if (!beforeUrl || !afterUrl || before.uploading || after.uploading) {
      setError('Add both the empty room and the staged room first.');
      return;
    }
    setError(null);
    setStarting(true);
    const idempotencyKey = idempotencyRef.current ?? crypto.randomUUID();
    idempotencyRef.current = idempotencyKey;
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const accessToken = sessionData.session?.access_token;
      if (!accessToken) {
        setError('You must be logged in to generate an animation.');
        return;
      }
      const { data, error: invokeError } = await supabase.functions.invoke('imagemotion-staging-animation', {
        body: {
          action: 'start',
          idempotencyKey,
          source,
          sourceImageId,
          projectName,
          beforeImageUrl: beforePath ? null : beforeUrl,
          afterImageUrl: afterPath ? null : afterUrl,
          beforeStoragePath: beforePath,
          afterStoragePath: afterPath,
          style: nextStyle,
          durationSeconds: duration,
          aspectRatio: ratio,
          motion,
        },
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (invokeError) {
        const message = await getFunctionInvokeErrorMessage(invokeError, 'Could not start the animation');
        setError(message);
        trackImagemotionEvent('staging_animation_failed', userId, { source });
        return;
      }
      const next = (data as { job?: StagingAnimationJob; error?: string })?.job;
      if (!next) {
        setError((data as { error?: string })?.error || 'Could not start the animation');
        return;
      }
      setJob(next);
      setStyle(nextStyle);
      idempotencyRef.current = null;
      const url = `/staging-animation?id=${next.id}`;
      window.history.replaceState({}, '', url);
      trackImagemotionEvent('staging_animation_started', userId, {
        source,
        style: nextStyle,
        duration,
        aspect_ratio: ratio,
        motion,
      });
      onCreditsUpdated?.();
    } finally {
      setStarting(false);
    }
  }

  function resetComposer() {
    setJob(null);
    setBefore(emptyFrame());
    setAfter(emptyFrame());
    setError(null);
    idempotencyRef.current = null;
    window.history.replaceState({}, '', '/staging-animation');
  }

  const busy = starting || job?.status === 'processing' || job?.status === 'queued';
  const showResult = job?.status === 'completed' && job.output_url;

  return (
    <div className="animate-fade-in max-w-5xl mx-auto">
      <button type="button" onClick={onBack} className="text-sm text-slate-400 hover:text-white mb-4">
        Back to projects
      </button>
      <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="flex items-center gap-3 mb-1">
            <h1 className="sr-only">Staging Animation</h1>
            <img
              src="/staging-animation-logo.png"
              alt=""
              className="block h-9 sm:h-11 w-auto max-w-none object-contain"
            />
            {source === 'virtual_staging' && (
              <span className="text-[11px] uppercase tracking-wide text-indigo-300 bg-indigo-500/10 border border-indigo-500/30 rounded-full px-2 py-0.5">
                Created from Virtual Staging
              </span>
            )}
          </div>
          <p className="text-slate-400">Animate an empty room into a fully staged space.</p>
          <button
            type="button"
            onClick={() => setExampleOpen(true)}
            className="mt-3 inline-flex items-center gap-2 text-sm text-slate-200 hover:text-white"
          >
            <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-white/10 ring-1 ring-white/20">
              <Play className="h-3.5 w-3.5 ml-0.5" fill="currentColor" />
            </span>
            See an example
          </button>
        </div>
        <button
          type="button"
          onClick={() => void openStagingApp()}
          aria-label="Open Virtual Staging"
          className="inline-flex shrink-0 items-center gap-2 rounded-lg border border-slate-700 bg-slate-950 px-3 py-1.5 hover:bg-slate-900"
        >
          <img
            src="/virtual-staging-logo-transparent.png"
            alt=""
            className="block h-8 w-auto max-w-none object-contain"
          />
          <ExternalLink className="h-3.5 w-3.5 text-white opacity-80" />
        </button>
      </div>

      {!settings.enabled && (
        <p className="mb-4 text-sm text-amber-300">Staging Animation is currently turned off.</p>
      )}

      {exampleOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4"
          onClick={() => setExampleOpen(false)}
          role="dialog"
          aria-modal="true"
          aria-label="Staging animation example"
        >
          <div
            className="relative w-full max-w-4xl"
            onClick={(event) => event.stopPropagation()}
          >
            <button
              type="button"
              onClick={() => setExampleOpen(false)}
              className="absolute -top-10 right-0 inline-flex items-center gap-1 text-sm text-white"
            >
              <X className="h-4 w-4" /> Close
            </button>
            <video
              src="/staging-animation-example.mp4"
              poster="/staging-animation-example.jpg"
              controls
              autoPlay
              playsInline
              className="w-full rounded-xl bg-black"
            />
          </div>
        </div>
      )}

      {showResult ? (
        <>
          {error && <p className="text-sm text-red-400 mb-3">{error}</p>}
          <Result
            job={job}
            busy={starting}
            beforeUrl={job.before_image_url}
            afterUrl={job.after_image_url}
            compareOpen={compareOpen}
            onCompare={() => setCompareOpen((open) => !open)}
            onDownload={async () => {
              if (!job.output_url) return;
              await downloadVideo(job.output_url, 'staging-animation.mp4');
              trackImagemotionEvent('staging_animation_downloaded', userId, { source: job.source });
            }}
            onAgain={() => {
              idempotencyRef.current = crypto.randomUUID();
              void generate(job.style);
            }}
            onChangeStyle={(next) => {
              idempotencyRef.current = crypto.randomUUID();
              void generate(next);
            }}
            onCreateAnother={resetComposer}
            styles={styles}
          />
        </>
      ) : busy ? (
        <RenderingScreen
          status={job?.status === 'queued' || job?.status === 'processing' ? job.status : 'starting'}
          startedAt={job?.status === 'queued' || job?.status === 'processing' ? job.created_at : null}
          beforeUrl={before.preview || job?.before_image_url || null}
          afterUrl={after.preview || job?.after_image_url || null}
          styleLabel={styles.find((item) => item.id === (job?.style ?? style))?.label ?? job?.style ?? style}
          duration={job?.duration_seconds ?? duration}
          ratio={job?.aspect_ratio ?? ratio}
          motion={job?.motion_level ?? motion}
        />
      ) : (
        <>
          {!before.preview && !after.preview && (
            <p className="text-slate-400 mb-4">
              Upload an empty room and its staged version to create an animated transformation.
            </p>
          )}
          <div className="grid grid-cols-1 md:grid-cols-[1fr_auto_1fr] gap-4 items-center mb-4">
            <FrameDrop
              label="Empty Room"
              hint="Upload the original unstaged image."
              frame={before}
              onFile={(file) => void onFile('before', file)}
              onClear={() => setBefore(emptyFrame())}
            />
            <div className="hidden md:flex flex-col items-center text-slate-400 text-xs font-semibold tracking-wide">
              <span>EMPTY</span>
              <ArrowRight className="w-5 h-5 my-1" />
              <span>STAGED</span>
            </div>
            <p className="md:hidden text-center text-xs font-semibold tracking-wide text-slate-400">EMPTY → STAGED</p>
            <FrameDrop
              label="Staged Room"
              hint="Upload the furnished / virtually staged version."
              frame={after}
              onFile={(file) => void onFile('after', file)}
              onClear={() => setAfter(emptyFrame())}
            />
          </div>

          {match.title && (
            <div className="mb-4 rounded-lg border border-amber-500/40 bg-amber-500/10 p-3 text-sm text-amber-100">
              <p className="font-semibold flex items-center gap-2">
                <AlertTriangle className="w-4 h-4" /> {match.title}
              </p>
              <p className="text-amber-100/80 mt-1">{match.body}</p>
            </div>
          )}

          <section className="space-y-4 mb-6">
            <div>
              <p className="text-xs uppercase tracking-wide text-slate-500 mb-2">Style</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                {styles.map((option) => (
                  <button
                    key={option.id}
                    type="button"
                    onClick={() => setStyle(option.id)}
                    className={`text-left px-3 py-2 rounded-lg border ${
                      style === option.id
                        ? 'border-blue-500 bg-blue-600/20 text-white'
                        : 'border-slate-700 bg-slate-900 text-slate-300'
                    }`}
                  >
                    <span className="block text-sm font-medium">
                      {option.label}
                      {option.recommended && <span className="ml-2 text-[10px] text-blue-300">Recommended</span>}
                    </span>
                    <span className="block text-xs text-slate-400 mt-0.5">{option.description}</span>
                  </button>
                ))}
              </div>
              <p className="text-xs text-slate-500 mt-2">
                {style === 'furniture_removal'
                  ? 'Furniture leaves until the room matches the empty photo. Walls stay the same.'
                  : style === 'people_placing'
                    ? 'Movers carry in only the furniture from the staged photo, set it down, then leave. Walls stay the same.'
                    : 'Only furniture and decor already in the staged photo are animated. Nothing new is added over windows or walls.'}
              </p>
            </div>
            <OptionRow label="Length">
              {durations.map((seconds) => (
                <Choice key={seconds} active={duration === seconds} onClick={() => setDuration(seconds)} label={`${seconds}s`} />
              ))}
            </OptionRow>
            <OptionRow label="Format">
              {ratios.map((option) => (
                <Choice key={option.id} active={ratio === option.id} onClick={() => setRatio(option.id)} label={option.label} />
              ))}
            </OptionRow>
            <OptionRow label="Motion">
              {MOTION_OPTIONS.map((option) => (
                <Choice key={option.id} active={motion === option.id} onClick={() => setMotion(option.id)} label={option.label} />
              ))}
            </OptionRow>
          </section>

          {error && <p className="text-sm text-red-400 mb-3">{error}</p>}
          {job?.status === 'failed' && (
            <p className="text-sm text-red-400 mb-3">{job.error_message || 'Generation failed. Credits were refunded if they were charged.'}</p>
          )}

          <button
            type="button"
            disabled={busy || !settings.enabled}
            onClick={() => void generate()}
            className="inline-flex items-center gap-2 px-5 py-3 rounded-lg bg-blue-600 text-white font-semibold hover:bg-blue-700 disabled:opacity-50"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
            {busy ? 'Generating animation' : `Generate Animation · ${settings.credit_cost} credits`}
          </button>
        </>
      )}
    </div>
  );
}

function FrameDrop({
  label,
  hint,
  frame,
  onFile,
  onClear,
}: {
  label: string;
  hint: string;
  frame: FrameState;
  onFile: (file: File) => void;
  onClear: () => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  return (
    <div
      className="rounded-xl border border-dashed border-slate-700 bg-slate-900/60 p-4 min-h-[240px]"
      onDragOver={(event) => event.preventDefault()}
      onDrop={(event) => {
        event.preventDefault();
        const file = event.dataTransfer.files?.[0];
        if (file) onFile(file);
      }}
    >
      <p className="text-white font-medium">{label}</p>
      <p className="text-xs text-slate-400 mb-3">{hint}</p>
      {frame.preview ? (
        <div className="relative">
          <img src={frame.preview} alt="" className="w-full h-48 object-contain rounded-lg bg-black" />
          {frame.uploading && (
            <div className="absolute inset-0 flex items-center justify-center bg-black/50 rounded-lg">
              <Loader2 className="w-6 h-6 animate-spin text-white" />
            </div>
          )}
          <div className="flex gap-2 mt-2">
            <button type="button" className="text-xs text-slate-300 underline" onClick={() => inputRef.current?.click()}>
              Replace
            </button>
            <button type="button" className="text-xs text-slate-300 underline inline-flex items-center gap-1" onClick={onClear}>
              <X className="w-3 h-3" /> Remove
            </button>
          </div>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          className="w-full h-40 rounded-lg bg-slate-800 text-slate-300 flex flex-col items-center justify-center gap-2"
        >
          <Upload className="w-5 h-5" />
          JPG, PNG, or WEBP
        </button>
      )}
      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        className="hidden"
        onChange={(event) => {
          const file = event.target.files?.[0];
          if (file) onFile(file);
          event.target.value = '';
        }}
      />
    </div>
  );
}

function OptionRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <p className="text-xs uppercase tracking-wide text-slate-500 mb-2">{label}</p>
      <div className="flex flex-wrap gap-2">{children}</div>
    </div>
  );
}

function Choice({
  active,
  label,
  hint,
  onClick,
}: {
  active: boolean;
  label: string;
  hint?: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`px-3 py-2 rounded-lg border text-sm ${
        active ? 'border-blue-500 bg-blue-600/20 text-white' : 'border-slate-700 bg-slate-900 text-slate-300'
      }`}
    >
      {label}
      {hint && <span className="block text-[10px] text-blue-300">{hint}</span>}
    </button>
  );
}

function formatElapsed(totalSeconds: number) {
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
}

function RenderingScreen({
  status,
  startedAt,
  beforeUrl,
  afterUrl,
  styleLabel,
  duration,
  ratio,
  motion,
}: {
  status: 'starting' | 'queued' | 'processing';
  startedAt: string | null;
  beforeUrl: string | null;
  afterUrl: string | null;
  styleLabel: string;
  duration: number;
  ratio: string;
  motion: string;
}) {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, []);

  const startedMs = startedAt ? new Date(startedAt).getTime() : null;
  const elapsed = startedMs ? Math.max(0, Math.floor((now - startedMs) / 1000)) : 0;

  const title =
    status === 'starting' ? 'Starting your render' : status === 'queued' ? 'Waiting in the queue' : 'Rendering your animation';
  const steps: { id: typeof status; label: string }[] = [
    { id: 'starting', label: 'Starting' },
    { id: 'queued', label: 'Queued' },
    { id: 'processing', label: 'Rendering' },
  ];
  const activeIndex = steps.findIndex((step) => step.id === status);

  return (
    <div
      className="rounded-2xl border border-slate-800 bg-slate-900/60 p-6 sm:p-10"
      role="status"
      aria-live="polite"
      aria-busy="true"
    >
      <div className="flex flex-col items-center text-center">
        <div className="flex items-center gap-3 mb-8">
          {beforeUrl ? (
            <img src={beforeUrl} alt="Empty room" className="w-28 h-20 sm:w-40 sm:h-28 object-cover rounded-lg bg-black" />
          ) : (
            <div className="w-28 h-20 sm:w-40 sm:h-28 rounded-lg bg-slate-800" />
          )}
          <div className="flex flex-col items-center text-blue-400">
            <Loader2 className="w-6 h-6 animate-spin" />
          </div>
          {afterUrl ? (
            <img src={afterUrl} alt="Staged room" className="w-28 h-20 sm:w-40 sm:h-28 object-cover rounded-lg bg-black" />
          ) : (
            <div className="w-28 h-20 sm:w-40 sm:h-28 rounded-lg bg-slate-800" />
          )}
        </div>

        <h2 className="text-xl font-semibold text-white">{title}</h2>
        <p className="text-sm text-slate-400 mt-1">
          This usually takes a few minutes.{startedMs ? ` Elapsed ${formatElapsed(elapsed)}.` : ''}
        </p>

        <div className="w-full max-w-md mt-6 h-1.5 rounded-full bg-slate-800 overflow-hidden">
          <div
            className="h-full bg-blue-500 animate-pulse transition-all duration-700"
            style={{ width: `${((activeIndex + 1) / steps.length) * 100}%` }}
          />
        </div>
        <ol className="w-full max-w-md mt-3 flex justify-between text-xs">
          {steps.map((step, index) => (
            <li
              key={step.id}
              className={index < activeIndex ? 'text-slate-400' : index === activeIndex ? 'text-white font-medium' : 'text-slate-600'}
            >
              {step.label}
            </li>
          ))}
        </ol>

        <p className="mt-6 text-sm text-slate-300">
          {styleLabel} · {duration}s · {ratio} · {motion}
        </p>
        <p className="mt-4 text-xs text-slate-500 max-w-md">
          You can leave this page. Your render keeps going, and you can reopen it from your projects to see the result.
        </p>
      </div>
    </div>
  );
}

function Result({
  job,
  busy,
  beforeUrl,
  afterUrl,
  compareOpen,
  onCompare,
  onDownload,
  onAgain,
  onChangeStyle,
  onCreateAnother,
  styles,
}: {
  job: StagingAnimationJob;
  busy: boolean;
  beforeUrl: string | null;
  afterUrl: string | null;
  compareOpen: boolean;
  onCompare: () => void;
  onDownload: () => void;
  onAgain: () => void;
  onChangeStyle: (style: StagingAnimationStyle) => void;
  onCreateAnother: () => void;
  styles: { id: StagingAnimationStyle; label: string; description: string }[];
}) {
  const styleLabel = styles.find((item) => item.id === job.style)?.label ?? job.style;
  return (
    <div className="space-y-4">
      <video src={job.output_url ?? undefined} controls className="w-full rounded-xl bg-black max-h-[70vh]" />
      <div className="flex gap-3 items-center text-sm text-slate-300">
        {beforeUrl && <img src={beforeUrl} alt="Before" className="w-16 h-16 object-cover rounded" />}
        {afterUrl && <img src={afterUrl} alt="After" className="w-16 h-16 object-cover rounded" />}
        <div>
          <p>{styleLabel}</p>
          <p className="text-slate-500">{job.duration_seconds}s · {job.aspect_ratio} · {job.motion_level}</p>
        </div>
      </div>
      {compareOpen && beforeUrl && afterUrl && (
        <div className="grid grid-cols-2 gap-2">
          <img src={beforeUrl} alt="Empty room" className="w-full rounded-lg" />
          <img src={afterUrl} alt="Staged room" className="w-full rounded-lg" />
        </div>
      )}
      <div className="flex flex-wrap gap-2">
        <button type="button" onClick={onDownload} className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-blue-600 text-white">
          <Download className="w-4 h-4" /> Download Video
        </button>
        <button
          type="button"
          onClick={onAgain}
          disabled={busy}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-slate-800 text-white disabled:opacity-50"
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
          {busy ? 'Generating…' : 'Generate Again'}
        </button>
        <button type="button" onClick={onCompare} className="px-4 py-2 rounded-lg bg-slate-800 text-white">
          Compare Before & After
        </button>
        <button type="button" onClick={onCreateAnother} className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-slate-800 text-white">
          <Film className="w-4 h-4" /> Create Another
        </button>
      </div>
      <div className="flex flex-wrap gap-2">
        {styles.map((option) => (
          <button
            key={option.id}
            type="button"
            onClick={() => onChangeStyle(option.id)}
            disabled={busy}
            title={option.description}
            className={`text-xs px-3 py-1.5 rounded-full border ${
              option.id === job.style
                ? 'border-blue-500 bg-blue-600/20 text-white'
                : 'border-slate-700 text-slate-300'
            }`}
          >
            {option.label}
          </button>
        ))}
      </div>
    </div>
  );
}
